import type { Character } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import type { Item } from '@/schemas/equipment';
import { STAT_IDS, type StatId } from '@/schemas/stats';
import { dwrClassesById } from '@/data/classes';
import { SheetChip } from '@/components/atoms';
import { SheetRow, SheetStat } from '@/components/molecules';
import { displayTagsForItem } from '@/utils/itemDisplay';
import { FullSheetMoves } from './FullSheetMoves';
import { FullSheetFollowers } from './FullSheetFollowers';
import { FullSheetSpells } from './FullSheetSpells';
import { SheetSection } from './SheetSection';
import { cn } from '@/lib/utils';

// organism — DWR FullSheet: scheda PG completa visualizzata nell'Hub.
// Orchestra 7 sezioni (Identità, Caratteristiche, Aspetti, Equipaggiamento,
// Mosse, Incantesimi se caster, Legami) componendo SheetSection + SheetRow
// + SheetStat + SheetChip. Mosse delegate a `<FullSheetMoves>` (sub-organism
// che usa `<MoveCard>` shared con setup compilato inline). Seguaci delegati
// a `<FullSheetFollowers>` (sezione condizionale post-FI-61 fix).
// Pattern .full-sheet mockup (styles.css righe 1780-1784): container
// paper-bright bordo rule + padding generoso + gap verticale tra sezioni.

interface FullSheetProps {
  character: Character;
  className?: string;
}

export function FullSheet({ character, className }: FullSheetProps) {
  const classDef = dwrClassesById[character.classId];

  return (
    <article
      className={cn(
        'bg-paper-bright border-rule flex flex-col gap-5 border px-3 py-3 sm:gap-[1.3rem] sm:px-[1.8rem] sm:py-[1.5rem]',
        className,
      )}
      aria-label={`Scheda del personaggio ${character.name}`}
    >
      <IdentitySection character={character} />
      <StatsSection character={character} />
      <AspettiSection character={character} />
      <EquipmentSection character={character} />
      <FullSheetMoves character={character} />
      <FullSheetFollowers character={character} />
      {classDef?.spellcasting ? <FullSheetSpells character={character} /> : null}
      <BondsSection character={character} />
    </article>
  );
}

// ---------------------------------------------------------------------------
// Sub-sezioni (page-specific, not reusable: orchestrazione interna a FullSheet)
// ---------------------------------------------------------------------------

function IdentitySection({ character }: { character: Character }) {
  const classDef = dwrClassesById[character.classId];
  return (
    <SheetSection title="Identità">
      <SheetRow label="Nome">
        {character.name}
        {character.title ? <span className="italic"> {character.title}</span> : null}
      </SheetRow>
      <SheetRow label="Classe">{classDef?.name ?? character.classId}</SheetRow>
      <SheetRow label="Razza">{raceLabel(character)}</SheetRow>
      <SheetRow label="Archetipo">{archetypeLabel(character, classDef)}</SheetRow>
      <SheetRow label="Allineamento">{alignmentLabel(character)}</SheetRow>
      <SheetRow label="Livello">{character.level}</SheetRow>
    </SheetSection>
  );
}

function raceLabel(character: Character): string {
  if (character.race.kind === 'preset') return character.race.id;
  return `${character.race.name} (custom)`;
}

function archetypeLabel(character: Character, classDef: ClassDefinition | undefined): string {
  const primary = classDef?.archetypes.find((a) => a.id === character.archetypeId);
  const primaryName = primary?.name ?? character.archetypeId;
  if (!character.secondaryArchetypeId) return primaryName;
  const secondary = classDef?.archetypes.find((a) => a.id === character.secondaryArchetypeId);
  const secondaryName = secondary?.name ?? character.secondaryArchetypeId;
  return `${primaryName} + ${secondaryName}`;
}

function alignmentLabel(character: Character) {
  return (
    <>
      {capitalize(character.alignment.id)}
      {character.alignment.precept ? (
        <span className="text-ink-soft italic"> — {character.alignment.precept}</span>
      ) : null}
    </>
  );
}

function StatsSection({ character }: { character: Character }) {
  const baseStats = computeOriginalStats(character);
  return (
    <SheetSection title="Caratteristiche">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-[repeat(auto-fit,minmax(110px,1fr))]">
        {STAT_IDS.map((statId) => (
          <SheetStat
            key={statId}
            statId={statId}
            value={character.stats[statId]}
            baseValue={baseStats[statId]}
          />
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <SheetChip label="Danno" value={character.damage} />
        <SheetChip label="PF max" value={character.hp.max} />
        <SheetChip label="Carico max" value={character.load.max} />
        <SheetChip label="Armatura" value={character.armor} />
      </div>
    </SheetSection>
  );
}

function AspettiSection({ character }: { character: Character }) {
  if (character.chosenAspetti.length === 0) {
    return (
      <SheetSection title="Aspetti">
        <SheetRow label="Nessun aspetto">
          <em>—</em>
        </SheetRow>
      </SheetSection>
    );
  }
  return (
    <SheetSection title="Aspetti">
      {character.chosenAspetti.map((aspetto) => (
        <SheetRow key={aspetto.categoria} label={aspetto.categoria}>
          {aspetto.valore || <em>—</em>}
        </SheetRow>
      ))}
    </SheetSection>
  );
}

function EquipmentSection({ character }: { character: Character }) {
  if (character.inventory.length === 0) {
    return (
      <SheetSection title="Equipaggiamento">
        <p className="text-ink-soft italic">Nessun oggetto in inventario.</p>
      </SheetSection>
    );
  }
  return (
    <SheetSection title="Equipaggiamento">
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[0.92rem]">
        {character.inventory.map((item) => (
          <FullSheetItem key={item.id} item={item} />
        ))}
      </ul>
    </SheetSection>
  );
}

/**
 * **FI-49 + FI-50 (2026-05-22)** — riga item per la scheda visualizzata.
 * Mostra:
 *  - name + quantity (se >1)
 *  - tag strutturati via `displayTagsForItem` (FI-49: oggi mostrava solo
 *    `item.tags.join(', ')`, perdendo weight/damage/armor/uses/armorPiercing).
 *  - description narrativa del catalogo (se presente).
 *  - userDescription del giocatore come quote/italic (FI-50).
 */
function FullSheetItem({ item }: { item: Item }) {
  const tags = displayTagsForItem(item);
  return (
    <li className="before:text-ink-soft relative flex flex-col gap-0.5 pl-2.5 before:absolute before:left-0 before:content-['▸']">
      <span>
        <strong>{item.name}</strong>
        {item.quantity && item.quantity > 1 ? <span> ×{item.quantity}</span> : null}
        {tags.length > 0 ? (
          <span className="text-ink-faint text-xs"> ({tags.join(', ')})</span>
        ) : null}
      </span>
      {item.description ? (
        <span className="text-ink-soft text-[0.85rem] italic">{item.description}</span>
      ) : null}
      {item.userDescription ? (
        <span className="text-ink border-ink-soft mt-0.5 border-l-2 pl-2 text-[0.88rem] italic">
          {item.userDescription}
        </span>
      ) : null}
    </li>
  );
}

function BondsSection({ character }: { character: Character }) {
  if (character.bondsDeferred) {
    return (
      <SheetSection title="Legami">
        <p className="text-ink-soft italic">Rinviati alla prima sessione.</p>
      </SheetSection>
    );
  }
  if (character.bonds.length === 0) {
    return (
      <SheetSection title="Legami">
        <p className="text-ink-soft italic">Nessun legame compilato.</p>
      </SheetSection>
    );
  }
  return (
    <SheetSection title="Legami">
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[0.92rem]">
        {character.bonds.map((bond, i) => (
          <li
            key={i}
            className="before:text-ink-soft relative pl-2.5 before:absolute before:left-0 before:content-['▸']"
          >
            {bond.text}
          </li>
        ))}
      </ul>
    </SheetSection>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Ricostruisce le stat originali (L1) sottraendo gli incrementi cumulativi
 * registrati in `levelUpHistory`. Usato per il flag `bumped` di <SheetStat>.
 */
function computeOriginalStats(character: Character): Record<StatId, number> {
  const base = { ...character.stats };
  for (const record of character.levelUpHistory) {
    if (record.statIncreased) {
      base[record.statIncreased] = base[record.statIncreased] - 1;
    }
  }
  return base;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
